'use client';

import { useState, useEffect, useMemo, useRef } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useFirestoreNFTActivity } from './useFirestoreNFTActivity';
import { collectionsSubscription } from './market-data-subscriptions';

// TypeScript interfaces based on the Firestore data structure
export interface FirestoreCollectionData {
  slug: string;
  name: string;
  image?: string;
  description?: string;
  url?: string;
  totalEvents?: number;
  recentSales?: number;
  recentListings?: number;
  totalVolume?: number;
  lastActivity?: string;
  featured?: boolean;      // NEW: Featured status for admin control
  hasAdSpot?: boolean;     // NEW: Ad spot control
  addAdCard?: boolean;     // NEW: Add promotional CTA cards in carousel
  featuredImage?: string;  // NEW: Admin-controlled featured image URL
  priority?: number;       // NEW: Display priority for featured collections
}

interface UseFirestoreCollectionsOptions {
  enableRealTimeUpdates?: boolean;
}

interface UseFirestoreCollectionsReturn {
  collections: string[];
  collectionsData: FirestoreCollectionData[];
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  isRealTime: boolean;
}

export function useFirestoreCollections(options: UseFirestoreCollectionsOptions = {}): UseFirestoreCollectionsReturn {
  const { enableRealTimeUpdates = true } = options;

  const [collections, setCollections] = useState<string[]>([]);
  const [collectionsData, setCollectionsData] = useState<FirestoreCollectionData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  useEffect(() => {
    // Skip during build/SSR — the shared poller fetches a browser-only route.
    if (!enableRealTimeUpdates || typeof window === 'undefined') {
      setLoading(false);
      return;
    }

    setError(null);

    // Attach to the ONE shared market-snapshot poller for the `collections` slice.
    // Each mount used to open its own whole-collection onSnapshot; they now share a single
    // reference-counted 60s poll of the cached /api/market/snapshot route, so N components
    // reading collections = 0 per-browser Firestore reads.
    const unsubscribe = collectionsSubscription.subscribe(
      (list) => {
        setCollections(list.map((c) => c.slug));
        setCollectionsData(list);
        setLastUpdated(new Date());
        setError(null);
        setLoading(false);
      },
      (err) => {
        console.warn('Firestore collections listener error:', err.message);
        setError(`Failed to fetch collections: ${err.message}`);
        setLoading(false);
      },
    );

    return unsubscribe;
  }, [enableRealTimeUpdates]);

  return {
    collections,
    collectionsData,
    loading,
    error,
    lastUpdated,
    isRealTime: enableRealTimeUpdates,
  };
}

// Helper function to get a specific collection by slug
export function useFirestoreCollection(collectionSlug: string) {
  const [collectionData, setCollectionData] = useState<FirestoreCollectionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!collectionSlug || !db) {
      setLoading(false);
      return;
    }

    const fetchCollection = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!db) {
          throw new Error('Database not initialized');
        }

        const docRef = doc(db, 'collections', collectionSlug);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data() as Omit<FirestoreCollectionData, 'slug'>;
          setCollectionData({
            slug: collectionSlug,
            ...data,
          });
        } else {
          setCollectionData(null);
          setError('Collection not found');
        }
      } catch (err) {
        setError(`Failed to fetch collection: ${err instanceof Error ? err.message : 'Unknown error'}`);
      } finally {
        setLoading(false);
      }
    };

    fetchCollection();
  }, [collectionSlug]);

  return {
    collectionData,
    loading,
    error,
  };
}

// NEW: Hook to get collections from NFT activity data (fallback when collections metadata is missing)
export function useCollectionsFromActivity() {
  const { events, loading: eventsLoading } = useFirestoreNFTActivity({
    limit: 1000, // Get more events to capture all collections
  });

  const [collections, setCollections] = useState<string[]>([]);
  const [collectionsData, setCollectionsData] = useState<FirestoreCollectionData[]>([]);

  // CoinTelegraph-style data processing: Debounced + Memoized + Smart caching
  const processedCollections = useMemo(() => {
    if (eventsLoading || !events.length) {
      return { collections: [], collectionsData: [] };
    }

    // Extract unique collections from NFT activity (optimized processing)
    const collectionMap = new Map<string, {
      slug: string;
      name: string;
      totalEvents: number;
      recentSales: number;
      recentListings: number;
      lastActivity: string;
    }>();

    // Process events efficiently - avoid reprocessing unchanged data
    events.forEach(event => {
      const { collection: slug, collectionName } = event;
      if (!slug) return;

      const existing = collectionMap.get(slug) || {
        slug,
        name: collectionName || slug,
        totalEvents: 0,
        recentSales: 0,
        recentListings: 0,
        lastActivity: event.timestamp,
      };

      existing.totalEvents++;
      if (event.eventType === 'sold') existing.recentSales++;
      if (event.eventType === 'listed') existing.recentListings++;
      
      // Update last activity if this event is more recent
      if (new Date(event.timestamp) > new Date(existing.lastActivity)) {
        existing.lastActivity = event.timestamp;
      }

      collectionMap.set(slug, existing);
    });

    // Convert to arrays (memoized - only recalculates when events actually change)
    const collectionsArray = Array.from(collectionMap.keys());
    const collectionsDataArray = Array.from(collectionMap.values()).map(data => ({
      ...data,
      featured: false, // Default to not featured
      hasAdSpot: false, // Default to no ad spot
      priority: 0, // Default priority
    }));

    return { 
      collections: collectionsArray, 
      collectionsData: collectionsDataArray 
    };
  }, [events, eventsLoading]);

  // Update state only when processed data actually changes (debounced effect)
  useEffect(() => {
    setCollections(processedCollections.collections);
    setCollectionsData(processedCollections.collectionsData);
  }, [processedCollections]);

  return {
    collections,
    collectionsData,
    loading: eventsLoading,
    error: null,
    lastUpdated: new Date(),
    isRealTime: true,
  };
}
